import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  CircularProgress, Alert, IconButton, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';
import RenewalPaymentFlow from './RenewalPaymentFlow';
import MobileSubscriptionInfo from './MobileSubscriptionInfo';

interface OpenRenewalCaseResponse {
  hasCase: boolean;
  token?: string;
  totalAmount?: number;
  currency?: string;
  expiryDate?: string | null;
}

interface LicensePaymentDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Panel içi ödeme popup'ı (header'daki demo/lisans rozetine tıklanınca açılır).
 *
 * Açılınca GET /api/Company/open-renewal-case ile çağıran şirketin AKTİF tahsilat
 * kaydını sorgular:
 *  - Kayıt varsa: paylaşılan RenewalPaymentFlow ile PayTR ödemesi yürütülür. Ödeme
 *    onaylanınca refreshCompany() çağrılır (rozet anında "Lisanslı Ürün"e döner).
 *  - Kayıt yoksa: "iletişime geçin" mesajı gösterilir (tahsilat dosyasını süperadmin açar).
 */
const LicensePaymentDialog: React.FC<LicensePaymentDialogProps> = ({ open, onClose }) => {
  const { refreshCompany } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caseInfo, setCaseInfo] = useState<OpenRenewalCaseResponse | null>(null);

  const loadOpenCase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await mainApi.get('/api/Company/open-renewal-case');
      setCaseInfo(res?.data as OpenRenewalCaseResponse);
    } catch (err) {
      setError('Ödeme bilgisi alınamadı. Lütfen tekrar deneyin.');
      setCaseInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadOpenCase();
    } else {
      // Kapanışta durumu sıfırla ki tekrar açılınca taze sorgulansın.
      setCaseInfo(null);
      setError(null);
      setLoading(true);
    }
  }, [open, loadOpenCase]);

  const handlePaid = useCallback(() => {
    // Ödeme onaylandı → şirket bilgisini tazele (demo temizlendi, lisans başladı).
    void refreshCompany();
  }, [refreshCompany]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaymentsIcon color="primary" />
          <span>Lisans / Ödeme</span>
        </Stack>
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <MobileSubscriptionInfo />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        ) : caseInfo?.hasCase && caseInfo.token ? (
          <RenewalPaymentFlow token={caseInfo.token} onPaid={handlePaid} onClose={onClose} />
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Ödeme için sizi bekleyen bir tutar yok
            </Typography>
            <Typography color="text.secondary">
              Lisans satın alımı veya yenileme için henüz bir ödeme kaydı oluşturulmamış.
              Kesintisiz kullanım ve lisanslı ürüne geçiş için lütfen bizimle iletişime geçin;
              tutarınız tanımlandığında ödemenizi buradan güvenle tamamlayabilirsiniz.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LicensePaymentDialog;
